import { MonoTypeOperatorFunction, Observable, catchError, tap, throwError } from 'rxjs';

/**
 * Used to perform side-effects for notifications or errors from the source observable
 *
 * This operator is similar to {@link https://rxjs.dev/api/operators/finalize | finalize} and
 * {@link https://rxjs.dev/api/operators/tap | tap} but, unlike the former, it executes on each notification, instead of
 * at the completion of the source observable, and, unlike the latter, it executes even if an error occurs.
 *
 * ## Examples
 *
 * ```ts
 * import { always } from '@se/se.ui.html5';
 *
 * this.waitingService.isWaiting = true;
 * http.get('url').pipe(always(() => (this.waitingService.isWiting = false)))
 * ```
 * @see {@link https://rxjs.dev/api/operators/finalize}
 * @see {@link https://rxjs.dev/api/operators/tap}
 *
 * @param fn The function to execute. It will be called with either an error or the notified value
 * @return A function that returns an Observable identical to the source, but runs the specified callback for each item.
 */
export function tapAlways<T>(fn: (value?: T | Error) => void): MonoTypeOperatorFunction<T> {
    return function (source: Observable<T>) {
        return source.pipe(
            tap((value) => fn(value)),
            catchError((err) => {
                fn(err);
                return throwError(() => err);
            }),
        );
    };
}
